import {
  Backdrop,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import usePageTitle from "../../../Common/hooks/usePageTitle";
import routes from "../../../Common/consts/routes";
import TopBar from "../../../Layout/components/TopBar/TopBar";
import { useFigmaCleanup } from "../hooks/useFigmaCleanup";
import FigmaCleanupReportsGrid from "../components/FigmaCleanupReportsGrid";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AdminUsersGrid from "../../UserSearch/components/AdminUsersGrid";
import { useToolsCapabilities } from "../../UserSearch/hooks/useToolsCapabilities";
import { useAdminToolUserSearch } from "../../UserSearch/hooks/useAdminToolUserSearch";
import { ToolInfoDto } from "../../UserSearch/api/models/ToolInfoDto";
import AdminToolFilter from "../../UserSearch/components/AdminToolFilter";
import AdminToolUsersGrid from "../../UserSearch/components/AdminToolUsersGrid";
import {
  exportToolUsers,
  syncToolUsers,
} from "../../UserSearch/api/adminToolUserSearchClient";

import AdminBannerConfigGrid from "../../Banners/components/AdminBannerConfigGrid";
import BannerConfigForm from "../../Banners/components/BannerConfigForm";
import { useAdminBannerConfig } from "../../Banners/hooks/useAdminBannerConfig";
import { useJiraWorkflows } from "../../JiraWorkflows/hooks/useJiraWorkflows";
import { ProjectViewModel } from "../../JiraWorkflows/api/jiraWorkflowsClient";
import debounce from "lodash/debounce";
import {
  Autocomplete,
  autocompleteClasses,
  Link,
  Paper,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import styles from "./AdminOperationsView.module.scss";

interface TabPanelProps {
  readonly children?: React.ReactNode;
  readonly index: number;
  readonly value: number;
}

function CustomTabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`operations-tabpanel-${index}`}
      aria-labelledby={`operations-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

const AdminOperationsView = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    reports,
    isLoading: isFigmaLoading,
    isUploading,
    uploadResult,
    error: figmaError,
    fetchReports,
    uploadCsv,
    clearUploadResult,
    clearError: clearFigmaError,
  } = useFigmaCleanup();

  // Tool Search tab state
  const { tools, isLoading: isToolsLoading } = useToolsCapabilities();
  const {
    fetchToolUsers,
    isLoading: isToolUsersLoading,
    data: toolUsersData,
  } = useAdminToolUserSearch();
  const [selectedTool, setSelectedTool] = useState<ToolInfoDto | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Tools/Users Sync tab state
  const [selectedSyncTool, setSelectedSyncTool] =
    useState<ToolInfoDto | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConfluenceSyncDialogOpen, setIsConfluenceSyncDialogOpen] =
    useState(false);

  // Banners tab state
  const {
    banners,
    isLoading: isBannersLoading,
    editing,
    startEdit,
    cancelEdit,
    save,
    remove,
  } = useAdminBannerConfig();

  // Jira Workflows tab state
  const [jiraOptions, setJiraOptions] = useState<readonly ProjectViewModel[]>(
    [],
  );
  const [jiraValue, setJiraValue] = useState<ProjectViewModel | null>(null);
  const [jiraInputValue, setJiraInputValue] = useState("");
  const [isJiraLoading, setIsJiraLoading] = useState(false);
  const {
    loadProjects,
    selectedProjectId,
    setSelectedProjectId,
    isCopying,
    handleCopyWorkflowScheme,
    copiedWorkflowScheme,
  } = useJiraWorkflows();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Map tab names to indices
  const tabMap: { [key: string]: number } = {
    "figma-cleanup": 0,
    "user-search": 1,
    "tool-search": 2,
    "tools-users-sync": 3,
    banners: 4,
    "jira-workflows": 5,
  };
  const tabPathMap: { [key: number]: string } = {
    0: routes.admin.operationsFigmaCleanup,
    1: routes.admin.operationsUserSearch,
    2: routes.admin.operationsToolSearch,
    3: routes.admin.operationsToolsUsersSync,
    4: routes.admin.operationsBanners,
    5: routes.admin.operationsJiraWorkflows,
  };

  const getTabFromPath = (path: string) => {
    const segments = path.split("/").filter(Boolean);
    const segment = segments[segments.length - 1];
    if (!segment || segment === "operations") {
      return 0;
    }
    return tabMap[segment] ?? 0;
  };

  const [tabValue, setTabValue] = useState(getTabFromPath(location.pathname));

  usePageTitle("Admin | Operations");

  useEffect(() => {
    if (tabValue === 0) {
      fetchReports();
    }
  }, [tabValue]);

  useEffect(() => {
    setTabValue(getTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (tabValue === 2 && selectedTool) {
      fetchToolUsers(
        selectedTool.toolKey,
        selectedTool.capabilityId,
        page + 1,
        pageSize,
      );
    }
  }, [selectedTool, page, pageSize, tabValue]);

  // Jira workflows autocomplete effect
  const fetch = debounce(
    async (
      input: string,
      callback: (results?: readonly ProjectViewModel[]) => void,
    ) => {
      const results = await loadProjects(input.trim());
      callback(results);
    },
    500,
  );

  useEffect(() => {
    if (tabValue !== 4) return;

    const minCharTyped = 3;

    if (jiraInputValue === "") {
      setJiraOptions(jiraValue ? [jiraValue] : []);
      return undefined;
    }

    let active = true;
    if (jiraInputValue.length < minCharTyped) {
      setJiraOptions([]);
      return undefined;
    } else {
      setIsJiraLoading(true);

      fetch(jiraInputValue, (projects) => {
        if (!active) {
          return;
        }

        let newOptions: readonly ProjectViewModel[] = [];

        if (projects) {
          newOptions = projects;

          if (jiraValue) {
            newOptions = [
              jiraValue,
              ...projects.filter((project) => project.name !== jiraValue.name),
            ];
          }
        } else if (jiraValue) {
          newOptions = [jiraValue];
        }

        setJiraOptions(newOptions);
        setIsJiraLoading(false);
      });
    }
    return () => {
      active = false;
    };
  }, [jiraValue, jiraInputValue, tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    navigate(tabPathMap[newValue] ?? routes.admin.operationsFigmaCleanup);
  };

  const handleToolSelect = (tool: ToolInfoDto) => {
    setSelectedTool(tool);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async () => {
    if (selectedTool) {
      await exportToolUsers(selectedTool.toolKey, selectedTool.capabilityId);
    }
  };

  const handleSyncToolSelect = (tool: ToolInfoDto) => {
    setSelectedSyncTool(tool);
  };

  const handleSync = () => {
    if (!selectedSyncTool) return;
    if (
      selectedSyncTool.capabilityToolKey === "WIKI:CONFLUENCE" ||
      selectedSyncTool.toolKey === "WIKI:CONFLUENCE"
    ) {
      setIsConfluenceSyncDialogOpen(true);
    } else {
      void performSync();
    }
  };

  const performSync = async () => {
    if (selectedSyncTool) {
      setIsSyncing(true);
      try {
        await syncToolUsers(
          selectedSyncTool.toolKey,
          selectedSyncTool.capabilityId,
        );
        enqueueSnackbar("Sync export completed successfully.", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Sync export failed. Please try again.";
        enqueueSnackbar(message, {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const handleConfluenceSyncConfirm = () => {
    setIsConfluenceSyncDialogOpen(false);
    void performSync();
  };

  const handleConfluenceSyncCancel = () => {
    setIsConfluenceSyncDialogOpen(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      clearUploadResult();
      clearFigmaError();
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleTriggerCleanup = async () => {
    if (selectedFile) {
      await uploadCsv(selectedFile);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const isLoading =
    isFigmaLoading ||
    isBannersLoading ||
    isToolsLoading ||
    isToolUsersLoading ||
    isSyncing;

  return (
    <div
      id="adminoperations"
      data-testid="adminoperations"
      style={{ height: "100%" }}
    >
      <TopBar>
        <div className={styles.details__topBarContainer}>
          <Typography variant="h4">Operations</Typography>
        </div>
      </TopBar>

      <Box
        sx={{ borderBottom: 1, borderColor: "divider", pl: "80px", pt: "24px" }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="operations tabs"
        >
          <Tab label="Figma Cleanup" id="operations-tab-0" />
          <Tab label="User Search" id="operations-tab-1" />
          <Tab label="Tool Search" id="operations-tab-2" />
          <Tab label="Tools/Users Sync" id="operations-tab-3" />
          <Tab label="Banners" id="operations-tab-4" />
          <Tab label="Jira Workflows" id="operations-tab-5" />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        <Box sx={{ position: "relative", py: 3, pl: "80px", pr: 3 }}>
          {tabValue === 0 && (isFigmaLoading || isUploading) && (
            <Backdrop
              sx={{
                position: "absolute",
                inset: 0,
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
              open
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          {/* Upload Section */}
          <Box className={styles.uploadSection}>
            <Typography variant="h6" gutterBottom>
              Upload Figma Members CSV
            </Typography>
            <Typography variant="body2" className={styles.uploadDescription}>
              Upload a CSV file exported from Figma containing member data.
              Users inactive for 4+ weeks will receive a warning email. Users
              inactive for 6+ weeks will be removed from the Figma AD group.
            </Typography>
            <Box className={styles.uploadActions}>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={handleUploadClick}
                disabled={isUploading}
              >
                Select CSV File
              </Button>
              {selectedFile && (
                <>
                  <Typography className={styles.selectedFile}>
                    {selectedFile.name}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTriggerCleanup}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Trigger Cleanup"
                    )}
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Alerts */}
          {uploadResult && (
            <Box className={styles.alertContainer}>
              <Alert
                severity={uploadResult.success ? "success" : "error"}
                onClose={clearUploadResult}
              >
                {uploadResult.message}
                {uploadResult.success && uploadResult.usersParsed > 0 && (
                  <> ({uploadResult.usersParsed} users will be processed)</>
                )}
              </Alert>
            </Box>
          )}

          {figmaError && (
            <Box className={styles.alertContainer}>
              <Alert severity="error" onClose={clearFigmaError}>
                {figmaError}
              </Alert>
            </Box>
          )}

          {/* Reports Grid */}
          <FigmaCleanupReportsGrid reports={reports} />
        </Box>
      </CustomTabPanel>

      {/* User Search Tab */}
      <CustomTabPanel value={tabValue} index={1}>
        <Box sx={{ py: 3, pl: "80px", pr: 3 }}>
          <AdminUsersGrid />
        </Box>
      </CustomTabPanel>

      {/* Tool Search Tab */}
      <CustomTabPanel value={tabValue} index={2}>
        <Box sx={{ py: 3, pl: "80px", pr: 3 }}>
          <Box sx={{ mb: 3 }}>
            <AdminToolFilter
              tools={tools}
              selectedToolKey={selectedTool?.capabilityToolKey || null}
              onSelect={handleToolSelect}
            />
          </Box>
          {selectedTool ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button variant="contained" onClick={handleExport}>
                  Export to CSV
                </Button>
              </Box>
              <AdminToolUsersGrid
                users={toolUsersData.items}
                totalCount={toolUsersData.totalCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <Typography variant="h6" sx={{ mt: 2 }}>
              Please select a tool to view users.
            </Typography>
          )}
        </Box>
      </CustomTabPanel>

      <Dialog
        open={isConfluenceSyncDialogOpen}
        onClose={handleConfluenceSyncCancel}
      >
        <DialogTitle>Confluence Sync</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Syncing Confluence will remove licenses from users who are no longer
            part of the project. Please make sure this is intended before
            proceeding.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfluenceSyncCancel}>Cancel</Button>
          <Button
            onClick={handleConfluenceSyncConfirm}
            variant="contained"
            color="primary"
          >
            Confirm Sync
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tools/Users Sync Tab */}
      <CustomTabPanel value={tabValue} index={3}>
        <Box sx={{ py: 3, pl: "80px", pr: 3 }}>
          <Box sx={{ mb: 3 }}>
            <AdminToolFilter
              tools={tools}
              selectedToolKey={selectedSyncTool?.capabilityToolKey || null}
              onSelect={handleSyncToolSelect}
            />
          </Box>
          {selectedSyncTool ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleSync}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sync"
                )}
              </Button>
            </Box>
          ) : (
            <Typography variant="h6" sx={{ mt: 2 }}>
              Please select a tool to sync users/projects.
            </Typography>
          )}
        </Box>
      </CustomTabPanel>

      {/* Banners Tab */}
      <CustomTabPanel value={tabValue} index={4}>
        {editing ? (
          <Box sx={{ py: 3, pl: "80px", pr: 3 }}>
            <BannerConfigForm
              initial={editing}
              onSubmit={save}
              onCancel={cancelEdit}
            />
          </Box>
        ) : (
          <>
            <Box sx={{ py: 3, pl: "80px", pr: 3, pb: 0 }}>
              <Box sx={{ mb: 3 }}>
                <Button variant="contained" onClick={() => startEdit("new")}>
                  Create Banner
                </Button>
              </Box>
            </Box>

            <AdminBannerConfigGrid
              banners={banners}
              onEdit={(id) => startEdit(id)}
              onDelete={(id) => remove(id)}
            />
          </>
        )}
      </CustomTabPanel>

      {/* Jira Workflows Tab */}
      <CustomTabPanel value={tabValue} index={5}>
        <Box sx={{ py: 3, pl: "80px", pr: 3 }}>
          <Paper sx={{ p: 3, maxWidth: "800px" }}>
            <Typography variant="h6" gutterBottom>
              Copy Workflow Scheme
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a project with Jira Cloud capability to copy its workflow
              scheme.
            </Typography>

            <Autocomplete
              sx={{
                marginRight: "8px",
                marginBottom: "16px",
                [`& .${autocompleteClasses.popupIndicator}`]: {
                  transform: "none",
                },
              }}
              getOptionLabel={(option) => option.name}
              filterOptions={(x) => x}
              options={jiraOptions}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={jiraValue}
              loading={isJiraLoading}
              noOptionsText="No projects found"
              onChange={(_event: any, newValue: ProjectViewModel | null) => {
                setJiraOptions(
                  newValue ? [newValue, ...jiraOptions] : jiraOptions,
                );
                setJiraValue(newValue);
                setSelectedProjectId(newValue ? newValue.id : "");
              }}
              onInputChange={(_event, newInputValue) => {
                setJiraInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  placeholder="Search project..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <div style={{ width: "100%" }}>
                    <Typography
                      display="inline-block"
                      variant={"subtitle2"}
                      sx={{
                        maxWidth: "100%",
                        height: 20,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.name}
                    </Typography>
                  </div>
                </Box>
              )}
            />

            <Button
              variant="contained"
              onClick={handleCopyWorkflowScheme}
              disabled={!selectedProjectId || isCopying}
              startIcon={<ContentCopyIcon />}
            >
              {isCopying ? (
                <>
                  Copying...
                  <CircularProgress
                    size={24}
                    color="inherit"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                </>
              ) : (
                "Copy workflow scheme"
              )}
            </Button>

            {copiedWorkflowScheme && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <strong>Successfully copied workflow scheme</strong>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Project:{" "}
                  <Link
                    href={copiedWorkflowScheme.projectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    fontWeight="bold"
                    color="primary"
                  >
                    {jiraValue?.name}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  New workflow scheme:{" "}
                  <Link
                    href={copiedWorkflowScheme.workflowSchemeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    fontWeight="bold"
                    color="primary"
                  >
                    {copiedWorkflowScheme.name}
                  </Link>
                </Typography>
              </Alert>
            )}
          </Paper>
        </Box>
      </CustomTabPanel>
    </div>
  );
};

export default AdminOperationsView;
